import { VStack } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import {
  computeListRecordRange,
  ErrorState,
  LoadingState,
  usePagination,
} from '@mangovoice/mango-ui'

import type { HotelsApi } from '../api/hotelsApi'
import { WakeupHistoryTable } from '../components/history/WakeupHistoryTable'
import type { DatePreset } from '../constants/datePresets'
import { HotelsI18nProvider, useHotelsI18n } from '../i18n'
import type { HotelsTranslationLike } from '../i18n'
import { useListExtensionLines, useListWakeupCallsAll } from '../queries/hooks'
import type { HospitalityPermissions } from '../types/rooms'
import {
  buildExtensionTimezoneMap,
  type ExtensionTimezoneMap,
} from '../utils/formatting'
import { filterWakeupHistoryCallsByPreset } from '../utils/wakeupHistoryDateFilter'
import {
  parseWakeupHistorySort,
  sortWakeupHistoryCalls,
  toWakeupHistorySortParam,
  type WakeupHistorySortDirection,
  type WakeupHistorySortField,
} from '../utils/wakeupHistorySorting'

interface WakeupHistoryPageProps {
  api: HotelsApi
  pbxId: number
  pbxUuid: string
  /** IANA PBX timezone (system settings), normalized in the host app. */
  pbxTimeZone: string
  permissions?: HospitalityPermissions
  t?: HotelsTranslationLike
  locale?: string
}

export const WakeupHistoryPage = ({
  t,
  locale,
  ...props
}: WakeupHistoryPageProps) => {
  return (
    <HotelsI18nProvider t={t} locale={locale}>
      <WakeupHistoryPageInner {...props} />
    </HotelsI18nProvider>
  )
}

const WakeupHistoryPageInner = ({
  api,
  pbxId,
  pbxUuid,
  pbxTimeZone,
}: WakeupHistoryPageProps) => {
  const { text, locale } = useHotelsI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const [datePreset, setDatePreset] = useState<DatePreset>('today')
  const [searchQuery, setSearchQuery] = useState('')
  const sortParam = searchParams.get('sb')
  const sortState = useMemo(
    () => parseWakeupHistorySort(sortParam),
    [sortParam],
  )

  const extensionLinesQuery = useListExtensionLines({
    api,
    pbxId,
    options: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  })

  const wakeupQuery = useListWakeupCallsAll({
    api,
    params: {
      pbx_id: pbxUuid,
      include_deleted: true,
    },
    extensionLines: extensionLinesQuery.data,
    options: {
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  })

  const extensionTzMap: ExtensionTimezoneMap = useMemo(
    () =>
      extensionLinesQuery.data
        ? buildExtensionTimezoneMap(extensionLinesQuery.data, pbxTimeZone)
        : {},
    [extensionLinesQuery.data, pbxTimeZone],
  )

  // --- All hooks MUST be above early returns (Rules of Hooks) ---

  const allCalls = useMemo(
    () =>
      Array.isArray(wakeupQuery.data?.results) ? wakeupQuery.data.results : [],
    [wakeupQuery.data?.results],
  )

  // Filter by date preset
  const dateFiltered = useMemo(
    () => filterWakeupHistoryCallsByPreset(allCalls, datePreset, pbxTimeZone),
    [allCalls, datePreset, pbxTimeZone],
  )

  // Filter by search (room number, room name, status)
  const searchFiltered = useMemo(() => {
    if (!searchQuery.trim()) return dateFiltered
    const q = searchQuery.toLowerCase().trim()
    return dateFiltered.filter(
      (wc) =>
        wc.room_number?.toLowerCase().includes(q) ||
        wc.room_name?.toLowerCase().includes(q) ||
        wc.display_status?.toLowerCase().includes(q),
    )
  }, [dateFiltered, searchQuery])

  const sortedCalls = useMemo(
    () => sortWakeupHistoryCalls(searchFiltered, sortState, locale),
    [searchFiltered, sortState, locale],
  )

  const { pageSize, setPageSize, currentPage, setCurrentPage, offset, limit } =
    usePagination(sortedCalls.length, Math.max(sortedCalls.length, 1))

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, datePreset, sortParam, setCurrentPage])

  const paginatedCalls = useMemo(
    () => sortedCalls.slice(offset, offset + limit),
    [sortedCalls, offset, limit],
  )

  const recordRange = computeListRecordRange(
    currentPage,
    pageSize,
    sortedCalls.length,
  )
  const startItem = recordRange?.start ?? 0
  const endItem = recordRange?.end ?? 0

  const handleSortChange = (
    field: WakeupHistorySortField,
    direction: WakeupHistorySortDirection,
  ) => {
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.set('sb', toWakeupHistorySortParam({ field, direction }))
    setSearchParams(nextSearchParams, { replace: true })
  }

  // --- Early returns AFTER all hooks ---

  if (!pbxUuid || wakeupQuery.isLoading)
    return (
      <LoadingState
        title=""
        size="lg"
        ariaLabel={text('hotels.common.loading', 'Loading')}
      />
    )
  if (wakeupQuery.isError) {
    return (
      <ErrorState
        title={text(
          'hotels.errors.loadWakeupHistory',
          'Failed to load wake-up history',
        )}
        retryLabel={text('hotels.common.retry', 'Try again')}
        onRetry={() => wakeupQuery.refetch()}
      />
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      <WakeupHistoryTable
        wakeupCalls={paginatedCalls}
        totalCount={allCalls.length}
        filteredCount={sortedCalls.length}
        startItem={startItem}
        endItem={endItem}
        currentPage={currentPage}
        pageSize={pageSize}
        searchQuery={searchQuery}
        datePreset={datePreset}
        sortState={sortState}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearchQuery}
        onDatePresetChange={setDatePreset}
        onSortChange={handleSortChange}
        pbxTimeZone={pbxTimeZone}
        extensionTzMap={extensionTzMap}
      />
    </VStack>
  )
}
